import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { X, Trash2, CheckCircle2, Info, FileIcon, Loader2 } from 'lucide-react'
import { cn } from '../../lib/utils'

const fileUploadCardVariants = cva(
	'flex gap-3 relative',
	{
		variants: {
			state: {
				loading:
					'flex-col items-start overflow-hidden rounded-md border border-(--color-border) bg-(--color-popover) p-3',
				uploaded:
					'flex-col items-start overflow-hidden rounded-md border border-(--color-border) bg-(--color-popover) p-3',
				error:
					'flex-col items-start overflow-hidden rounded-md border border-(--color-border) bg-(--color-popover) p-3 ring-2 ring-(--color-background) ring-offset-2 ring-offset-(--color-destructive-hover)',
				idle:
					'items-center',
				disabled:
					'flex-col items-start overflow-hidden rounded-md border border-(--color-border) bg-(--color-popover) p-3',
			},
		},
		defaultVariants: { state: 'loading' },
	}
)

export type FileUploadState = 'loading' | 'uploaded' | 'error' | 'idle' | 'disabled'

export interface FileUploadCardProps
	extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
		VariantProps<typeof fileUploadCardVariants> {
	fileName: string
	fileSize: string
	previewSrc?: string
	progress?: number
	onRemove?: () => void
	onAction?: () => void
	actionLabel?: string
	removeLabel?: string
	loadingLabel?: string
	uploadedLabel?: string
	errorLabel?: string
}

function FileUploadCard({
	className,
	state = 'loading',
	fileName,
	fileSize,
	previewSrc,
	progress = 0,
	onRemove,
	onAction,
	actionLabel,
	removeLabel,
	loadingLabel,
	uploadedLabel,
	errorLabel,
	...props
}: FileUploadCardProps) {
	const showPreview = state !== 'loading'
	const showRemoveButton = state === 'loading' || state === 'uploaded' || state === 'error'
	const showActionLink = state === 'idle' || state === 'disabled'

	const preview = showPreview && previewSrc ? (
		<div className="size-10 shrink-0 overflow-hidden rounded-sm">
			<img
				src={previewSrc}
				alt={fileName}
				className="size-full object-cover"
			/>
		</div>
	) : (
		<div className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-(--color-secondary)">
			<FileIcon className="size-4 text-(--color-muted-foreground)" />
		</div>
	)

	const removeIcon = state === 'loading'
		? <X className="size-4" />
		: <Trash2 className={cn('size-4', state === 'error' && 'text-(--color-destructive)')} />

	const statusIndicator = (() => {
		switch (state) {
			case 'loading':
				return loadingLabel ? (
					<div className="flex items-center gap-1">
						<Loader2 className="size-4 animate-spin text-(--color-muted-foreground)" />
						<span className="text-sm leading-5 text-(--color-foreground)">{loadingLabel}</span>
					</div>
				) : null
			case 'uploaded':
				return uploadedLabel ? (
					<div className="flex items-center gap-1">
						<CheckCircle2 className="size-4 text-(--color-success)" />
						<span className="text-sm leading-5 text-(--color-foreground)">{uploadedLabel}</span>
					</div>
				) : null
			case 'error':
				return errorLabel ? (
					<div className="flex items-center gap-1">
						<Info className="size-4 text-(--color-destructive)" />
						<span className="text-sm leading-5 text-(--color-foreground)">{errorLabel}</span>
					</div>
				) : null
			default:
				return null
		}
	})()

	return (
		<div
			data-slot="file-upload-card"
			className={cn(fileUploadCardVariants({ state }), className)}
			{...props}
		>
			<div className="flex w-full items-center gap-3">
				{preview}
				<div className="flex min-w-0 flex-1 flex-col">
					<div className="flex items-center gap-1">
						<span className="flex-1 truncate text-base leading-6 text-(--color-foreground)">
							{fileName}
						</span>
						{showRemoveButton && (
							<button
								type="button"
								onClick={onRemove}
								className="flex shrink-0 items-center justify-center rounded-sm p-0.5"
								aria-label={removeLabel}
							>
								{removeIcon}
							</button>
						)}
					</div>
					<div className="flex items-start gap-2">
						<span className="shrink-0 text-sm leading-5 text-(--color-muted-foreground)">
							{fileSize}
						</span>
						{statusIndicator}
						{showActionLink && (
							<button
								type="button"
								onClick={onAction}
								className="text-sm font-medium leading-5 text-(--color-brand)"
							>
								{actionLabel}
							</button>
						)}
					</div>
				</div>
			</div>

			{state === 'loading' && (
				<div className="h-2 w-full overflow-hidden rounded-full bg-(--color-secondary)">
					<div
						className="h-full rounded-full bg-(--color-primary) transition-all"
						style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
					/>
				</div>
			)}
		</div>
	)
}

FileUploadCard.displayName = 'FileUploadCard'
export { FileUploadCard, fileUploadCardVariants }
